import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router';
import partnerOfferService, { PublicLinkData } from '../../../api/partner/partnerOfferService';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import Footer from '../../../components/Footer';
import { PublicLinkTable } from './components';
import { DEFAULT_PAGE_SIZE } from './constants';
import { LogoPartnerSidebarIc } from '../../../components/icons';

const PublicLinkPage: React.FC = () => {
  const { idLink } = useParams<{ idLink: string }>();
  const { t } = useTranslations();
  const [data, setData] = useState<PublicLinkData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(async () => {
    if (!idLink) return;

    try {
      setIsLoading(true);
      const response = await partnerOfferService.getPublicLinkData(idLink);
      // Map data to add id field for table compatibility
      const mappedData = response.objects.map((item: PublicLinkData) => ({
        ...item,
        id: item._id,
      }));
      setData(mappedData);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Error fetching public link data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [idLink]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  if (isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Typography variant="body1">{t('common.loading')}</Typography>
      </div>
    );
  }

  return (
    <div className="bg-white flex flex-col h-screen items-center min-h-screen w-full">
      {/* Header */}
      <div className="flex gap-4 items-center px-6 py-8 w-full md:max-w-[62.5rem]">
        <Link to="/partner/dashboard">
          <LogoPartnerSidebarIc />
        </Link>
        <Typography variant="h6" className="text-auth-ink">
          {t('partner.orders.publicLinksModal.title')}
        </Typography>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-6 px-6 w-full md:max-w-[62.5rem] pb-6">
        {/* Table */}
        <PublicLinkTable
          data={data}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Footer */}
      <div className="absolute w-full md:max-w-[62.5rem] flex items-start justify-start bottom-0">
        <Footer isPartner />
      </div>
    </div>
  );
};

export default PublicLinkPage;
